import type { FormFieldCondition, FormFieldConditionSingle, FormFieldConditionCompound } from '@/types/form'

export type ResolveIndexFn = (str: string, index?: number | string) => string

export function mapConditionalDisplayToCondition(cond: any): FormFieldCondition | undefined {
  if (!cond || typeof cond !== 'object') return undefined

  if (cond.rules && Array.isArray(cond.rules)) {
    const logic = cond.logic === 'any' ? 'any' : 'all'
    const rules: FormFieldConditionSingle[] = cond.rules
      .map((r: any) => {
        if (!r?.field || r?.operator == null) return null
        return {
          operator: r.operator,
          left: r.field,
          right: r.value,
        } as FormFieldConditionSingle
      })
      .filter(Boolean)
    if (rules.length === 0) return undefined
    return { logic, rules } as FormFieldConditionCompound
  }

  if (cond.field && cond.operator != null) {
    return {
      operator: cond.operator,
      left: cond.field,
      right: cond.value,
    } as FormFieldConditionSingle
  }

  return undefined
}

function isCompoundCondition(c: FormFieldCondition): c is FormFieldConditionCompound {
  return 'logic' in c && 'rules' in c && Array.isArray((c as FormFieldConditionCompound).rules)
}

function getValueFromContext(
  resolvedLeft: string,
  context: Record<string, any>,
  fallbackContext?: Record<string, any>,
): any {
  let value: any
  if (context && resolvedLeft) {
    if (resolvedLeft.includes('.')) {
      value = resolvedLeft.split('.').reduce((acc: any, part: string) => acc && acc[part], context)
    } else {
      value = context[resolvedLeft]
    }
  } else {
    value = undefined
  }
  if (value === undefined && fallbackContext && resolvedLeft) {
    if (resolvedLeft.includes('.')) {
      value = resolvedLeft.split('.').reduce((acc: any, part: string) => acc && acc[part], fallbackContext)
    } else {
      value = fallbackContext[resolvedLeft]
    }
  }
  return value
}

function evaluateSingleRule(
  rule: FormFieldConditionSingle,
  context: Record<string, any>,
  resolveIndex: ResolveIndexFn,
  index?: number | string,
  fallbackContext?: Record<string, any>,
): boolean {
  const resolvedLeft = resolveIndex(rule.left, index)
  const value = getValueFromContext(resolvedLeft, context, fallbackContext)

  const resolvedRight = resolveIndex(String(rule.right ?? ''), index)

  switch (rule.operator) {
    case 'equals':
      return value === resolvedRight || value === rule.right
    case 'not_equals':
      return value !== resolvedRight && value !== rule.right
    case 'contains': {
      const needle = index !== undefined ? resolvedRight : String(rule.right ?? '').replace(/\{\{index\}\}/g, '')
      const checkValue = (v: any) => {
        if (v === undefined || v === null) return false
        const s = String(v)
        if (index !== undefined) return s === needle
        return s.replace(/_site\d+$/, '').replace(/site\d+$/, '') === needle.replace(/_site\d*$/, '').replace(/site\d*$/, '')
      }
      if (Array.isArray(value)) return value.some(checkValue)
      return checkValue(value)
    }
    case 'not_contains':
      return !Array.isArray(value) || !value.includes(resolvedRight)
    case 'empty':
      return !value || (Array.isArray(value) && value.length === 0)
    case 'not_empty':
      return !!value && (!Array.isArray(value) || value.length > 0)
    default:
      return true
  }
}

export function evaluateCondition(
  condition: FormFieldCondition | undefined,
  context: Record<string, any>,
  resolveIndex: ResolveIndexFn,
  index?: number | string,
  fallbackContext?: Record<string, any>,
): boolean {
  if (!condition) return true

  const evalRule = (r: FormFieldConditionSingle) =>
    evaluateSingleRule(r, context, resolveIndex, index, fallbackContext)

  if (isCompoundCondition(condition)) {
    const results = condition.rules.map(evalRule)
    return condition.logic === 'all' ? results.every(Boolean) : results.some(Boolean)
  }

  return evalRule(condition)
}
